import {Map as IMap} from 'immutable';
import {camelCase} from 'lodash';
import {weakMemo} from '@packages/utilities/memo';
import type {
  NormalizedAtomConfig,
  ContextType,
  PluginsContext,
  ClassesList,
  GenerateAttributes,
} from '@packages/systems/wfdl';
import {EvaluatedNodeData} from '@packages/utilities/data-utils';
import {getDefaultClasses} from '@packages/systems/style/engine';

export type AttributeMap = IMap<string, any>;

// Memoized function builder to create `generateAttributes` prop for Atom Render functions.
export const createGenerateAttributes = weakMemo(
  function createGenerateAttributes_memoAtomConfig(
    atomConfig: NormalizedAtomConfig
  ) {
    return weakMemo(function createGenerateAttributes_memoNodeData(
      data: EvaluatedNodeData
    ) {
      return weakMemo(function createGenerateAttributes_memoPluginsContext(
        pluginsContext: PluginsContext
      ): GenerateAttributes {
        return function generateAttributes(attributes, classes) {
          const attributeMap = finalizeAttributeMap(
            data,
            pluginsContext,
            atomConfig,
            attributes,
            classes
          );
          // Convert the Immutable#Map to JS object so object spread can be used.
          return attributeMap.toJS();
        };
      });
    });
  }
);

// Retrieve the macro slug IX1 data-ix attribute.
function getIX1MacroSlug(
  data: EvaluatedNodeData,
  /**
   * Macro context should always be a Map, but there are renderer
   * instances that's manually setting this to `null`, like:
   * https://github.com/webflow/webflow/blob/15942c5b1e134fa2af2918c1c875085a68b0e56b/public/js/editor/EditorCanvas.jsx#L374
   * TODO: check whether this is necessary, and consolidate the type
   * for consistency
   */
  macroContext: null | ContextType
): string | null {
  // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
  const nodeMacroGuid = data.macro?.guid;
  if (nodeMacroGuid && macroContext) {
    return macroContext.getIn([nodeMacroGuid, 'slug'], null);
  }
  return null;
}

function convertStyleAttributeToCamelCase(
  style: IMap<string, string>
): IMap<string, string> {
  // @ts-expect-error - TS2740 - Type 'Keyed<string | undefined, string>' is missing the following properties from type 'Map<string, string>': set, delete, remove, clear, and 14 more.
  return style.mapKeys((key) => {
    if (key.indexOf('-') !== -1) {
      return camelCase(key);
    }
    return key;
  });
}

// Return a new "attributes" Immutable.Map with the "attributes.style" attribute
// converted into an Immutable.Map, if needed, and merged with the provided styles
// object literal.
export function mergeStyleAttributes(
  attributes: AttributeMap,
  styles: {
    [key: string]: string;
  }
): AttributeMap {
  // Immutable.Map.update won't make a change if the returned value is the same.
  // This prevents us from adding an "empty" style attribute to the attribtues Map.
  return attributes.update(
    'style',
    IMap(),
    (style_attribute: string | IMap<string, string>) => {
      let attr: string | IMap<string, string> = style_attribute;
      if (typeof attr === 'string') {
        // Split the style string on the ';' char removing whitespace.
        const dec_strings = attr.split(/\s*;(?:\s*)?/);
        const declarations = IMap<string, string>().asMutable();
        for (let i = 0; i < dec_strings.length; i++) {
          const dec_str = dec_strings[i];
          // Skip empty string caused by trailing ';' char.
          if (dec_str) {
            // Split the declaration string on the ':' char removing whitespace.
            const parts = dec_str.split(/\s*:\s*/);
            if (parts[0] && parts[1]) {
              declarations.set(parts[0], parts[1]);
            }
          }
        }
        attr = declarations.asImmutable();
      }
      return attr.merge(styles);
    }
  );
}

// Finalize the attributes map by:
// - adding the className attribute if needed
// - converting the style attribute to React format
// - adding the IX1 macro slug if needed
// - adding flow id if needed
function finalizeAttributeMap(
  data: EvaluatedNodeData,
  pluginsContext: PluginsContext,
  atomConfig: NormalizedAtomConfig,
  attrMap: AttributeMap,
  classes: ClassesList
): AttributeMap {
  // Get the Context for the plugin key.
  const pluginContext: ContextType =
    pluginsContext.get(atomConfig.pluginKey) || IMap();
  // Get the macro Context for IX1.
  const macroContext: ContextType = pluginsContext.get('macro') || IMap();
  // Get the finalized class list including default classes.
  const classList = finalizeClassList(data, pluginContext, atomConfig, classes);

  return attrMap.withMutations((mut_attrMap) => {
    if (classList.size > 0) {
      const classNameString = classList.join(' ');
      mut_attrMap.set('className', classNameString);
    }

    const macroSlug = getIX1MacroSlug(data, macroContext);
    if (macroSlug !== null) {
      mut_attrMap.set('data-ix', macroSlug);
    }

    if (attrMap.has('style')) {
      mut_attrMap.update('style', convertStyleAttributeToCamelCase);
    }
  });
}

// Finalize the class list by adding Atom default classes.
function finalizeClassList(
  data: EvaluatedNodeData,
  pluginContext: ContextType,
  atomConfig: NormalizedAtomConfig,
  classes: ClassesList
): ClassesList {
  // Get any default classes we need for this node.
  const defaultClasses = getDefaultClasses(data, pluginContext, atomConfig);
  // TODO: Check if the order is needed here. Technically it could matter if using class attribute selectors.
  // Add the default classes to the beginning of the Immutable.List.
  const withAtomClasses = classes.unshift(...defaultClasses);
  // Note: this is an internal hack for dynamic rich text fields for the edu/content team
  // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
  const customClass = data.customclass;
  let updatedClasses =
    customClass && typeof customClass === 'string'
      ? withAtomClasses.push(customClass)
      : withAtomClasses;
  // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
  if (data.xattr) {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    for (const attribute of data.xattr) {
      if ((attribute.name ?? '').toLowerCase().trim() === 'class') {
        const className = attribute.value ?? '';
        if (className) {
          updatedClasses = updatedClasses.push(className);
        }
      }
    }
  }

  return updatedClasses;
}

export const forTestSuite = {
  getIX1MacroSlug,
} as const;
